import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Badge, Box, Button, Center, Group, Loader, Stack, Text, Title } from "@mantine/core";
import { DoorOpen, Mic, MicOff, PhoneOff, Video, VideoOff } from "lucide-react";
import { toast } from "sonner";
import { usePortalEvent } from "@/lib/portal-socket";
import { GUIDES_KEY } from "@/lib/api/guides";
import { callApiError, doorbellCallKey, doorbellCallsApi, isNotATargetError, type DoorbellCallInfo } from "@/lib/api/doorbell-calls";
import { callStatusText, initialCallMode, type CallMode } from "@/lib/doorbell-call/call-status";
import { CALL_ENDED_EVENT, callIdOf } from "@/lib/doorbell-call/incoming";
import type { CallSnapshot } from "@/lib/doorbell-call/session";
import { activeCallId, useDoorbellCall } from "@/hooks/use-doorbell-call";
import { IncomingCallCard } from "@/components/doorbell/incoming-call-card";

/** While a call rings, refetch it this often — the safety net for a member
 *  whose portal socket dropped or missed the realtime `call.ended` push
 *  (`usePortalEvent` below is the instant path). */
export const RINGING_POLL_MS = 5_000;

function CallVideo({
  stream,
  muted = false,
  mirrored = false,
  testId,
}: {
  stream: MediaStream | null;
  muted?: boolean;
  mirrored?: boolean;
  testId: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream;
  }, [stream]);
  return (
    <Box
      component="video"
      ref={ref}
      autoPlay
      playsInline
      muted={muted}
      data-testid={testId}
      w="100%"
      bg="dark.9"
      style={{ aspectRatio: "4 / 3", objectFit: "cover", borderRadius: 12, transform: mirrored ? "scaleX(-1)" : undefined }}
    />
  );
}

export interface DoorbellCallViewProps {
  info: DoorbellCallInfo | undefined;
  loadError: boolean;
  isTest: boolean;
  snapshot: CallSnapshot;
  openingDoor: boolean;
  onAccept: (video: boolean) => void;
  declining: boolean;
  /** A 403 `not_a_target` on decline: this member was never rung, so the button disappears quietly. */
  declineHidden: boolean;
  onDecline: () => void;
  onMute: (muted: boolean) => void;
  onCamera: (on: boolean) => void;
  onHangup: () => void;
  onOpenDoor: () => void;
  onDone: () => void;
  onBack: () => void;
}

/** The call page — presentational. */
export function DoorbellCallView(props: DoorbellCallViewProps) {
  const { info, isTest, snapshot: s } = props;
  if (props.loadError) {
    return (
      <Center p="xl">
        <Stack align="center" gap="sm">
          <Text c="red" data-testid="call-status">
            This call could not be found.
          </Text>
          <Button variant="default" onClick={props.onBack}>
            Back to the portal
          </Button>
        </Stack>
      </Center>
    );
  }
  if (!info && s.phase === "idle") {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  const onCall = s.phase === "connected" || s.phase === "reconnecting";
  const busy = s.phase === "acquiring" || s.phase === "accepting";
  // Declining is final for this member (owner ruling): never offer to answer
  // again, even while the call still rings for other targets.
  const canAnswer = info?.state === "ringing" && !info?.declined_by_you && (s.phase === "idle" || s.phase === "ringing");
  const over = s.phase === "ended" || s.phase === "taken" || s.phase === "replaced" || s.phase === "failed";
  // Ringing / "choose how to answer": the same card the popup shows, title and
  // buttons included, so the generic header below doesn't repeat it.
  const choosing = canAnswer || busy;

  return (
    <Stack p="md" gap="lg" maw={720} mx="auto" data-testid="doorbell-call">
      <Group gap="xs">
        <Title order={2} fz="xl">
          Visitor call
        </Title>
        {isTest && !choosing && (
          <Badge color="grape" variant="light">
            Test call
          </Badge>
        )}
      </Group>
      {!choosing && (
        <Text fw={600} data-testid="call-status">
          {callStatusText(info, s, isTest)}
        </Text>
      )}
      {s.error && s.phase !== "failed" && (
        <Alert color="red" variant="light">
          {s.error}
        </Alert>
      )}

      {onCall && isTest && (
        <Stack gap="sm">
          <CallVideo stream={s.localStream} muted mirrored testId="self-view" />
          <Alert color="green" variant="light">
            Test OK — ringing, notifications, camera and microphone work
          </Alert>
          <Button onClick={props.onDone} w="fit-content">
            Done
          </Button>
        </Stack>
      )}

      {onCall && !isTest && (
        <Stack gap="sm">
          <Box pos="relative">
            <CallVideo stream={s.remoteStream} testId="visitor-video" />
            {s.camera && (
              <Box pos="absolute" right={12} bottom={12} w={120}>
                <CallVideo stream={s.localStream} muted mirrored testId="self-view" />
              </Box>
            )}
          </Box>
          {!s.mediaConnected && (
            <Text fz="sm" c="dimmed">
              Connecting the video…
            </Text>
          )}
          {s.remoteMuted && (
            <Text fz="sm" c="dimmed">
              The visitor has muted their microphone.
            </Text>
          )}
          <Group gap="sm" wrap="wrap">
            <Button
              variant="default"
              leftSection={s.muted ? <MicOff size={16} /> : <Mic size={16} />}
              onClick={() => props.onMute(!s.muted)}
            >
              {s.muted ? "Unmute" : "Mute"}
            </Button>
            <Button
              variant="default"
              leftSection={s.camera ? <VideoOff size={16} /> : <Video size={16} />}
              onClick={() => props.onCamera(!s.camera)}
            >
              {s.camera ? "Camera off" : "Camera on"}
            </Button>
            {info?.door_available && (
              <Button color="teal" leftSection={<DoorOpen size={16} />} loading={props.openingDoor} onClick={props.onOpenDoor}>
                Open door
              </Button>
            )}
            <Button color="red" leftSection={<PhoneOff size={16} />} onClick={props.onHangup}>
              Hang up
            </Button>
          </Group>
        </Stack>
      )}

      {choosing && (
        <IncomingCallCard
          askingFor={info?.asking_for ?? ""}
          location={info?.location ?? ""}
          isTest={isTest}
          onAnswer={props.onAccept}
          answerBusy={busy}
          decline={props.declineHidden ? undefined : { loading: props.declining, onDecline: props.onDecline }}
        />
      )}

      {(over || (!canAnswer && !busy && !onCall && info !== undefined)) && (
        <Button variant="default" onClick={props.onBack} w="fit-content">
          Back to the portal
        </Button>
      )}
    </Stack>
  );
}

/** `/doorbell/call/:id` — from the ringing banner, a web push or a Slack "Answer call" link.
 *  Keyed by the call id: moving to another call remounts the page, so the old
 *  call's session is torn down and the new one starts clean (mode, test
 *  variant and `?answer=` auto-answer included). */
export default function DoorbellCallPage() {
  const { id } = useParams<{ id: string }>();
  return <DoorbellCallScreen key={id} />;
}

function DoorbellCallScreen() {
  const { id } = useParams<{ id: string }>();
  const callId = Number(id);
  const valid = Number.isInteger(callId) && callId > 0;
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useSearchParams();
  const qc = useQueryClient();
  const firstPage = useRef(location.key === "default");

  const { data: info, isError } = useQuery({
    queryKey: doorbellCallKey(callId),
    queryFn: () => doorbellCallsApi.get(callId).then((r) => r.data),
    enabled: valid,
    retry: false,
    staleTime: 0,
    refetchInterval: (q) => (q.state.data?.state === "ringing" ? RINGING_POLL_MS : false),
  });

  // Instant path: someone else answered, declined or the visitor hung up —
  // refetch now so the answer buttons disappear and the outcome shows without
  // waiting for the poll above.
  usePortalEvent(CALL_ENDED_EVENT, (e) => {
    if (valid && callIdOf(e) === callId) void qc.invalidateQueries({ queryKey: doorbellCallKey(callId) });
  });

  // Decided once, when the call first loads: a later refetch must not restart the session.
  const [setup, setSetup] = useState<{ mode: CallMode; isTest: boolean } | null>(null);
  useEffect(() => {
    if (setup || !info) return;
    setSetup({ mode: initialCallMode(info, activeCallId() === callId), isTest: info.is_test });
  }, [setup, info, callId]);

  const call = useDoorbellCall({ callId, isTest: setup?.isTest ?? false, mode: setup?.mode ?? null });
  const { accept, hangup } = call;
  const { phase } = call.snapshot;

  // From the banner (`?answer=video|voice`): answer at once, one time.
  const answer = search.get("answer");
  const autoAnswered = useRef(false);
  useEffect(() => {
    if (autoAnswered.current || !answer || info?.state !== "ringing" || phase !== "ringing") return;
    autoAnswered.current = true;
    accept(answer === "video");
    setSearch({}, { replace: true, state: location.state });
  }, [answer, info?.state, phase, accept, setSearch, location.state]);

  // Over: show the server's view of it, and let the guide's Ring me step tick.
  useEffect(() => {
    if (phase !== "ended" && phase !== "taken" && phase !== "replaced" && phase !== "failed") return;
    void qc.invalidateQueries({ queryKey: doorbellCallKey(callId) });
    if (setup?.isTest) void qc.invalidateQueries({ queryKey: GUIDES_KEY });
  }, [phase, qc, callId, setup?.isTest]);

  const openDoor = useMutation({
    mutationFn: () => doorbellCallsApi.openDoor(callId),
    onSuccess: () => toast.success("Door opened"),
    onError: (err) => toast.error(callApiError(err, "Couldn't open the door.")),
  });

  // Same action as the popup's Decline — only a rung target may use it. A
  // member who landed here without being a target (e.g. a company-wide Slack
  // post) gets a quiet 403 `not_a_target`: hide the button, no toast.
  const [declineHidden, setDeclineHidden] = useState(false);
  const decline = useMutation({
    mutationFn: () => doorbellCallsApi.decline(callId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: doorbellCallKey(callId) }),
    onError: (err) => {
      if (isNotATargetError(err)) setDeclineHidden(true);
      else toast.error(callApiError(err, "Couldn't decline the call."));
    },
  });

  const done = () => {
    hangup();
    // Back where the member was (e.g. the guide); home when this was the first page.
    if (firstPage.current) navigate("/");
    else navigate(-1);
  };

  return (
    <DoorbellCallView
      info={info}
      loadError={!valid || isError}
      isTest={setup?.isTest ?? false}
      snapshot={call.snapshot}
      openingDoor={openDoor.isPending}
      onAccept={accept}
      declining={decline.isPending}
      declineHidden={declineHidden}
      onDecline={() => decline.mutate()}
      onMute={call.setMuted}
      onCamera={call.setCamera}
      onHangup={hangup}
      onOpenDoor={() => openDoor.mutate()}
      onDone={done}
      onBack={() => navigate("/")}
    />
  );
}
